// Карточка «Состав набора» на экране прогресса. Всё, что ОПИСЫВАЕТ сегодняшний
// набор, но ничего в нём не решает: сколько задач, каких и по каким темам.
//
// Была отдельной страницей — `src/screens/DaySet.tsx`, 14–20 сентября 2026, — с
// кнопкой «Что в наборе» рядом с «Начать». Страница ушла 20 сентября: «Сегодня»
// отвечает на «что делать сейчас», и вторая большая кнопка в том же ряду уводила
// от него в описание. Описание — это взгляд на себя, и живёт оно там, куда за
// этим и ходят: на «Прогрессе», свёрнутой секцией.
//
// Свёрнуто — две строки: сколько задач какого типа и сколько из них закрыто. Это
// ответ на «что мне сегодня выпало» целиком; темы после раскрытия, потому что
// день, набранный повторениями, даёт пятнадцать тем, а пятнадцать строк — уже не
// сводка.

import { useState } from "react";
import { type Assignment, type DayPlan, type ProgressSnapshot } from "../api";
import { plural } from "../lib/dates";
import { IconChallenge, IconChevron, IconNew, IconReview } from "./Icons";

/**
 * Минуты блока — доля от числа сервера, а не своя оценка. Своя разъехалась бы
 * с той, по которой планировщик собирал набор: у него 1,5 минуты на
 * повторение, и повторить это на экране значит завести второй источник истины.
 */
function share(part: Assignment[], all: Assignment[], total: number): number {
  if (all.length === 0) return 0;
  return Math.max(1, Math.round((total * part.length) / all.length));
}

function count(
  assignments: Assignment[],
  kind: Assignment["kind"],
): Assignment[] {
  return assignments.filter((a) => a.kind === kind);
}

function Block({
  icon,
  kind,
  title,
  note,
  minutes,
  done,
}: {
  icon: React.ReactNode;
  kind: Assignment["kind"];
  title: string;
  note: string;
  minutes: number;
  done: boolean;
}) {
  return (
    <div className={`block${done ? " block--done" : ""}`}>
      <div className={`block__icon block__icon--${kind}`}>{icon}</div>
      <div className="block__body">
        <div className="block__title">{title}</div>
        <div className="block__note">{note}</div>
      </div>
      <div className="block__aside num">
        {done ? "закрыто" : minutes > 0 ? `${minutes} мин` : "—"}
      </div>
    </div>
  );
}

type OpenSkill = ProgressSnapshot["openSkills"][number];
type Topic = DayPlan["topics"][number];

/**
 * Почему тема в наборе — значками из её типов и состояния навыка.
 *
 * Повторение попадает в набор двумя путями (жадное покрытие в
 * convex/engine/planner.ts): навыку самому пора повторить — или он
 * задействует навыки, которым пора, и одна его задача освежает их все. Второе
 * видно по тому, что сам навык не в долгу, хотя задач по нему сегодня не было.
 * Состояние — из снимка прогресса, то есть на момент открытия экрана.
 *
 * Словами причина стоит только в подсказке: пятнадцать строк с фразой под
 * каждой читались как текст, а не как список. Значки объясняет одна легенда.
 */
function topicMarks(t: Topic, s: OpenSkill | undefined): { key: string; tone: string; title: string; icon: React.ReactNode }[] {
  const marks = [];
  if (t.kinds.includes("review")) {
    if (s?.due === true)
      marks.push({ key: "due", tone: "due", title: "пора повторить: память просела", icon: <IconReview size={14} width={2.2} /> });
    else if (t.done === 0)
      marks.push({ key: "cover", tone: "cover", title: "задействует навыки, которым пора повторение", icon: <IconReview size={14} width={2.2} /> });
  }
  if (t.kinds.includes("new"))
    marks.push({ key: "new", tone: "new", title: "новый навык сегодня", icon: <IconNew size={14} width={2.2} /> });
  if (t.kinds.includes("challenge"))
    marks.push({ key: "challenge", tone: "challenge", title: "вызов: задача формата экзамена", icon: <IconChallenge size={14} width={2.2} /> });
  if (t.extra)
    marks.push({ key: "extra", tone: "extra", title: "из добавки", icon: <span className="topic__plus">+</span> });
  return marks;
}

function TopicLine({ topic, skill }: { topic: Topic; skill: OpenSkill | undefined }) {
  const tone = skill === undefined ? "" : skill.mastered ? " topic__level--done" : "";
  return (
    <div className="topic topic--row">
      <span className="topic__name">{topic.name}</span>
      <span className="topic__marks">
        {topicMarks(topic, skill).map((m) => (
          <span
            key={m.key}
            className={`topic__mark topic__mark--${m.tone}`}
            role="img"
            aria-label={m.title}
            title={m.title}
          >
            {m.icon}
          </span>
        ))}
      </span>
      <span
        className={`topic__level num${skill?.confidence === "none" ? " topic__level--guess" : tone}`}
        title={skill === undefined ? undefined : "владение, 0–100"}
      >
        {skill === undefined ? "—" : `${skill.confidence === "none" ? "≈" : ""}${skill.level}`}
      </span>
      <span className="topic__count num">
        {topic.done} / {topic.tasks}
      </span>
    </div>
  );
}

export function SetCard({
  plan,
  skills,
}: {
  plan: DayPlan | null | undefined;
  /** Открытые навыки из снимка прогресса — владение и долг по темам набора. */
  skills?: readonly OpenSkill[];
}) {
  const [open, setOpen] = useState(false);
  const [showSingles, setShowSingles] = useState(false);

  // Набор ещё не пришёл — карточки нет вовсе. Скелет здесь был бы обещанием
  // блока, который на экране прогресса и не главный: страница про карту, а не
  // про сегодняшний день.
  if (plan === undefined || plan === null) return null;

  const reviews = count(plan.assignments, "review");
  const news = count(plan.assignments, "new");
  const challenges = count(plan.assignments, "challenge");
  const doneCount = plan.assignments.filter((a) => a.done).length;
  const minutes = plan.minutes + plan.extraMinutes;
  const topicDay = plan.topic;
  const opened = plan.opened;
  const chosen = opened[0] ?? null;
  // Темы набора: по строке на тему с двумя задачами и больше, остальные одной
  // строкой. Схлопываются только темы с ОДНОЙ задачей: число тем стоит в
  // заголовке, число схлопнутых названо словами, и сумма по карточке
  // по-прежнему сходится с набором. Порог в три: схлопывать одну-две строки
  // значит прятать их ради строки той же длины.
  const singles =
    plan.topics.length - plan.topics.filter((t) => t.tasks > 1).length >= 3
      ? plan.topics.filter((t) => t.tasks === 1)
      : [];
  const listed = plan.topics.filter((t) => !singles.includes(t));
  const skillOf = new Map((skills ?? []).map((sk) => [sk.skillId, sk]));

  // Первая строка сводки — типы задач числами. Ради неё секцию и открывают:
  // «сколько сегодня повторений и есть ли вызов» — это весь состав набора, если
  // читать одну строку.
  const kinds: { label: string; n: number }[] = [];
  if (reviews.length > 0)
    kinds.push({
      label: topicDay === null ? "Повторение" : "Тема дня",
      n: reviews.length,
    });
  if (news.length > 0) kinds.push({ label: "Новый навык", n: news.length });
  if (challenges.length > 0)
    kinds.push({ label: "Вызов", n: challenges.length });

  return (
    <div className="card">
      <div className="card__head">
        <div className="card__label">
          Состав набора · {plan.assignments.length}{" "}
          {plural(plan.assignments.length, "задача", "задачи", "задач")}
        </div>
        <button
          type="button"
          className="btn btn--icon"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="secs__chev">
            <IconChevron size={14} width={2.2} />
          </span>
          {open ? "Свернуть" : "Развернуть"}
        </button>
      </div>

      <div className="setsum">
        <div className="setsum__kinds">
          {kinds.length === 0
            ? "Набор пуст"
            : kinds.map((k, i) => (
                <span key={k.label}>
                  {i > 0 ? " · " : ""}
                  {k.label} <span className="num">{k.n}</span>
                </span>
              ))}
        </div>
        <div className="faint">
          Сделано <span className="num">{doneCount}</span> из{" "}
          <span className="num">{plan.assignments.length}</span>
          {topicDay === null ? (
            plan.topics.length > 0 ? (
              <>
                {" · "}
                <span className="num">{plan.topics.length}</span>{" "}
                {plural(plan.topics.length, "тема", "темы", "тем")}
              </>
            ) : null
          ) : (
            <> · тема «{topicDay.name}»</>
          )}{" "}
          · <span className="num">{minutes}</span>{" "}
          {plural(minutes, "минута", "минуты", "минут")} — оценка, не норма
        </div>
      </div>

      {!open ? null : (
        <>
          <div className="stack" style={{ marginTop: 16 }}>
            {reviews.length > 0 ? (
              <Block
                icon={<IconReview color="oklch(0.45 0.13 150)" />}
                kind="review"
                title={`${topicDay === null ? "Повторение" : "Тема дня"} · ${reviews.length} ${plural(reviews.length, "задача", "задачи", "задач")}`}
                // В день по теме подпись «Возвращаем то, что начало забываться»
                // была бы неправдой дважды: возвращается не забытое, а
                // выбранное, и не расписанием, а учеником.
                note={
                  topicDay === null
                    ? "Возвращаем то, что начало забываться"
                    : topicDay.name
                }
                minutes={share(reviews, plan.assignments, minutes)}
                done={reviews.every((a) => a.done)}
              />
            ) : null}

            {/*
              Открывать нечего — блока нет. Пустая граница знания это обычное
              состояние дня, а не сбой, и пустой блок каждый день читался бы как
              неисправленная поломка. Само предложение выбрать навык живёт на
              «Сегодня»: это действие, а секция описывает набор.
            */}
            {news.length === 0 ? null : (
              <Block
                icon={<IconNew color="oklch(0.45 0.13 255)" />}
                kind="new"
                title={`Новый навык · ${news.length} ${plural(news.length, "задача", "задачи", "задач")}`}
                note={
                  chosen
                    ? opened.length > 1
                      ? `${chosen.name} · и ещё ${opened.length - 1}`
                      : chosen.name
                    : "Выбран сегодня"
                }
                minutes={share(news, plan.assignments, minutes)}
                done={news.every((a) => a.done)}
              />
            )}

            {challenges.length > 0 ? (
              <Block
                icon={<IconChallenge color="oklch(0.47 0.14 35)" />}
                kind="challenge"
                title="Вызов · 1 задача"
                note="Формат целевого экзамена — каждый день"
                minutes={share(challenges, plan.assignments, minutes)}
                done={challenges.every((a) => a.done)}
              />
            ) : null}
          </div>

          {/*
            Темы набора сводкой. Не заголовок над задачей: сводка называет темы
            дня и их веса, но не говорит, какая задача к какой теме относится, —
            и порядок в наборе остаётся перемешанным. Тем же правилом живёт
            список открытых за день навыков. Разбор — над `planTopics` в
            convex/lib/plan.ts.

            В день по теме сводки нет: тема одна, она уже названа блоком выше, а
            подпись «темы перемешаны» была бы прямой неправдой.
          */}
          {plan.topics.length > 0 && topicDay === null ? (
            <>
              <hr className="hr" />
              <div className="card__label">
                Темы набора · {plan.topics.length}
              </div>
              <div className="stack stack--tight" style={{ marginTop: 12 }}>
                {listed.map((t) => (
                  <TopicLine key={t.skillId} topic={t} skill={skillOf.get(t.skillId)} />
                ))}
                {singles.length > 0 ? (
                  <button
                    type="button"
                    className="topic topic--toggle"
                    aria-expanded={showSingles}
                    onClick={() => setShowSingles((v) => !v)}
                  >
                    <span className="topic__name muted">
                      <span className="secs__chev">
                        <IconChevron size={12} width={2.2} />
                      </span>
                      {showSingles ? "Свернуть" : "Ещё"} {singles.length}{" "}
                      {plural(singles.length, "тему", "темы", "тем")} по одной
                      задаче
                    </span>
                    <span className="topic__count num">
                      {singles.filter((t) => t.done > 0).length} /{" "}
                      {singles.length}
                    </span>
                  </button>
                ) : null}
                {showSingles
                  ? singles.map((t) => (
                      <TopicLine key={t.skillId} topic={t} skill={skillOf.get(t.skillId)} />
                    ))
                  : null}
              </div>
              <div className="faint" style={{ marginTop: 12, lineHeight: 1.6 }}>
                <span className="topic__mark topic__mark--due topic__mark--inline">
                  <IconReview size={13} width={2.2} />
                </span>{" "}
                пора повторить ·{" "}
                <span className="topic__mark topic__mark--cover topic__mark--inline">
                  <IconReview size={13} width={2.2} />
                </span>{" "}
                освежает навыки под ним ·{" "}
                <span className="topic__mark topic__mark--new topic__mark--inline">
                  <IconNew size={13} width={2.2} />
                </span>{" "}
                новый ·{" "}
                <span className="topic__mark topic__mark--challenge topic__mark--inline">
                  <IconChallenge size={13} width={2.2} />
                </span>{" "}
                вызов · <span className="topic__plus">+</span> добавка. Справа —
                владение 0–100 и сделано из задач темы. Какая задача к какой теме —
                не показано: темы перемешаны, чтобы способ решения узнавался по
                условию, а не по названию над задачей.
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
